import i18n from "../../../language/i18n";

// How a personalized assignment is NAMED in the learner's reports.
//
// The backend deliberately sends no display name for these rows (see
// PersonalizedAssignmentPerformanceItemDTO): it used to ship "Personalized Assignment #1672000" —
// English, and named after a database id. The row is named here instead, from the two facts that
// actually mean something to a learner: WHY they got the assignment (`triggerType`) and WHICH konu it
// targets (`categoryIdList`, heaviest weight first). The raw id moves to the ⓘ tooltip, which was
// empty on these rows because this DTO — alone among the performance DTOs — has no `description`.

/** Trigger → the phrase that opens the label. An unknown/absent trigger degrades to the plain kind. */
const TRIGGER_LABEL_KEY: Record<string, string> = {
  LOW_SCORE_MIX: "pa_label_low_score_mix",
  SUBJECT_DESCRIPTION: "pa_label_subject_description",
  BRANCH_ASSIGNMENT: "pa_label_branch_assignment",
  SUPPORT_ASSIGNMENT: "pa_label_support_assignment",
  DIAGNOSTIC: "pa_label_diagnostic",
  READINESS: "pa_label_readiness",
  STAFF_DIRECT: "pa_label_staff_direct",
};

export interface PersonalizedAssignmentNaming {
  /** categoryId → konu name, from the category list the user context already holds. */
  categoryNameById?: Map<number, string>;
  /** branchAssignmentId → its authored name, for a row triggered by a teacher's homework. */
  branchAssignmentNameById?: Map<number, string>;
}

const primaryCategoryName = (
  item: any,
  naming: PersonalizedAssignmentNaming,
): string | undefined => {
  if (!Array.isArray(item?.categoryIdList) || !naming.categoryNameById) {
    return undefined;
  }
  // The list arrives heaviest-first, so the first one that resolves is the assignment's main konu.
  for (const categoryId of item.categoryIdList) {
    const categoryName = naming.categoryNameById.get(Number(categoryId));
    if (categoryName) return categoryName;
  }
  return undefined;
};

/**
 * The row's İSİM cell. Only the triggers that point at something nameable get a suffix — a
 * "Zayıf konu tekrarı" spans several konu by definition, so appending one would misdescribe it.
 * READINESS targets exactly one prerequisite konu (its categoryIdList carries it), so it gets the
 * same konu suffix as SUBJECT_DESCRIPTION.
 */
export const personalizedAssignmentLabelOf = (
  item: any,
  naming: PersonalizedAssignmentNaming = {},
): string => {
  const labelKey = TRIGGER_LABEL_KEY[item?.triggerType] || "personalized_assignment";
  const base = i18n.t(labelKey);
  let suffix: string | undefined;
  if (
    item?.triggerType === "SUBJECT_DESCRIPTION" ||
    item?.triggerType === "READINESS"
  ) {
    suffix = primaryCategoryName(item, naming);
  } else if (item?.triggerType === "BRANCH_ASSIGNMENT" && item?.triggerId != null) {
    suffix = naming.branchAssignmentNameById?.get(Number(item.triggerId));
  } else if (item?.triggerType === "STAFF_DIRECT" && item?.coterieType) {
    // Direct-sent rounds hang on nothing nameable, so the ders — carried on the row — stands in.
    suffix = i18n.t(item.coterieType);
  }
  return suffix ? `${base}: ${suffix}` : base;
};

/** The ⓘ tooltip: the identifier support asks for, the size, and the lesson. */
export const personalizedAssignmentTooltipOf = (
  item: any,
  naming: PersonalizedAssignmentNaming = {},
): string => {
  const parts: string[] = [];
  if (item?.personalizedAssignmentId != null) {
    parts.push(`#${item.personalizedAssignmentId}`);
  }
  if (typeof item?.activityCount === "number" && item.activityCount > 0) {
    parts.push(`${item.activityCount} ${i18n.t("unit_questions")}`);
  }
  // Coterie first — the label above may already carry the konu, and repeating it wastes the tooltip.
  // A not-yet-evaluated row has no coterie (it is derived from evaluations), so the konu stands in.
  const lesson = item?.coterieType
    ? i18n.t(item.coterieType)
    : primaryCategoryName(item, naming);
  if (lesson) parts.push(lesson);
  return parts.join(" · ");
};
